import { calgaryDateString } from '../../../../utils/calgaryTime';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import GenExpensesService from "../../../../AppSettings/services/GenExpensesService";
import OppExpService from "../../../../AppSettings/services/OppExpService";
import VenExpService from "../../../../AppSettings/services/VenExpService";
import FixBillService from "../../../../AppSettings/services/FixBillService";
import MatterService from "../../../../AppSettings/services/MatterService";
import AgentsService from "../../../../AppSettings/services/AgentsService";
import toastService from "../../../../AppSettings/services/ToastService";
import FormEntryMultilineTextbox from "@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox";
import FormModal from "@frontend/Components/Modals/FormModal";
import "./LogExpenseEntryModal.css";

type ExpenseType = "general" | "operating" | "vendorServices" | "fixedBills";

interface LogExpenseEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LogExpenseEntryModal: React.FC<LogExpenseEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [expenseType, setExpenseType] = useState<ExpenseType>("general");
  const [matters, setMatters] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Common fields
  const [date, setDate] = useState(calgaryDateString());
  const [selectedMatter, setSelectedMatter] = useState<any>(null);
  const [matterSearch, setMatterSearch] = useState("");
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");

  const [showMatterDropdown, setShowMatterDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  const matterInputRef = useRef<HTMLInputElement>(null);
  const matterDropdownRef = useRef<HTMLDivElement>(null);
  const agentInputRef = useRef<HTMLInputElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  // Fixed Bills catalog state
  const [fbSearch, setFbSearch] = useState("");
  const [showCreateFb, setShowCreateFb] = useState(false);
  const [newFbDescription, setNewFbDescription] = useState("");
  const [newFbAmount, setNewFbAmount] = useState("");
  const [newFbTaxPercent, setNewFbTaxPercent] = useState("0");
  const [fbSelectedAgent, setFbSelectedAgent] = useState<any>(null);
  const [fbAgentSearch, setFbAgentSearch] = useState("");
  const [showFbAgentDropdown, setShowFbAgentDropdown] = useState(false);

  const fbAgentInputRef = useRef<HTMLInputElement>(null);
  const fbAgentDropdownRef = useRef<HTMLDivElement>(null);

  const {
    data: fixedBillsData,
    isLoading: fbLoading,
    error: fbError,
    refetch: refetchFb,
  } = useQuery({
    queryKey: ["fixedBillExpenses"],
    queryFn: FixBillService.getAllFixBillExpenses,
    enabled: isOpen && expenseType === "fixedBills",
  });

  const rawFb = (fixedBillsData as any)?.data ?? fixedBillsData;
  const fbList = Array.isArray(rawFb) ? rawFb : [];
  const filteredFb = fbList.filter((item: any) => {
    if (!fbSearch) return true;
    const q = fbSearch.toLowerCase();
    return (
      item.description?.toLowerCase().includes(q) ||
      item.matter_name?.toLowerCase().includes(q)
    );
  });

  // Reset form when modal closes so the next open shows a clean form
  const resetForm = useCallback(() => {
    setExpenseType("general");
    setError("");
    setDate(calgaryDateString());
    setSelectedMatter(null);
    setMatterSearch("");
    setSelectedAgent(null);
    setAgentSearch("");
    setDescription("");
    setAmount("");
    setTaxPercent("0");
    setShowMatterDropdown(false);
    setShowAgentDropdown(false);
    setFbSearch("");
    setShowCreateFb(false);
    setNewFbDescription("");
    setNewFbAmount("");
    setNewFbTaxPercent("0");
    setFbSelectedAgent(null);
    setFbAgentSearch("");
    setShowFbAgentDropdown(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Fetch matters and agents data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mattersData, agentsData] = await Promise.all([
          MatterService.getMatters(),
          AgentsService.getAgents(),
        ]);
        setMatters(mattersData || []);
        setAgents(agentsData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        matterDropdownRef.current &&
        matterInputRef.current &&
        !matterDropdownRef.current.contains(event.target as Node) &&
        !matterInputRef.current.contains(event.target as Node)
      ) {
        setShowMatterDropdown(false);
      }
      if (
        agentDropdownRef.current &&
        agentInputRef.current &&
        !agentDropdownRef.current.contains(event.target as Node) &&
        !agentInputRef.current.contains(event.target as Node)
      ) {
        setShowAgentDropdown(false);
      }
      if (
        fbAgentDropdownRef.current &&
        fbAgentInputRef.current &&
        !fbAgentDropdownRef.current.contains(event.target as Node) &&
        !fbAgentInputRef.current.contains(event.target as Node)
      ) {
        setShowFbAgentDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter matters based on search
  const filteredMatters = matters.filter((matter) =>
    `${matter.matterid}: ${matter.mattername || ""}`
      .toLowerCase()
      .includes(matterSearch.toLowerCase())
  );

  // Filter agents based on search
  const getAgentDisplayName = (agent: any): string => {
    let displayName = "";
    if (agent.FirstName && agent.LastName) {
      displayName = `${agent.FirstName} ${agent.LastName}`;
    } else if (agent.FirstName && !agent.LastName) {
      displayName = agent.FirstName;
    } else if (!agent.FirstName && agent.LastName) {
      displayName = agent.LastName;
    } else if (agent.ContactName) {
      displayName = agent.ContactName;
    } else {
      displayName = "Unknown Agent";
    }
    return `${displayName}${agent.Company ? ` - ${agent.Company}` : ""}`;
  };

  const filteredAgents = agents.filter((agent) =>
    getAgentDisplayName(agent)
      .toLowerCase()
      .includes(agentSearch.toLowerCase())
  );

  const handleMatterSelect = (matter: any) => {
    setSelectedMatter(matter);
    setMatterSearch(`${matter.matterid}: ${matter.mattername || ""}`);
    setShowMatterDropdown(false);
  };

  const handleAgentSelect = (agent: any) => {
    setSelectedAgent(agent);
    setAgentSearch(getAgentDisplayName(agent));
    setShowAgentDropdown(false);
  };

  const filteredFbAgents = agents.filter((agent) =>
    getAgentDisplayName(agent)
      .toLowerCase()
      .includes(fbAgentSearch.toLowerCase())
  );

  const handleFbAgentSelect = (agent: any) => {
    setFbSelectedAgent(agent);
    setFbAgentSearch(getAgentDisplayName(agent));
    setShowFbAgentDropdown(false);
  };

  const handleCreateFixedBill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fbSelectedAgent) {
      setError("Please select an agent");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FixBillService.addFixBillExpense({
        description: newFbDescription,
        amount: parseFloat(newFbAmount),
        tax_percent: parseFloat(newFbTaxPercent) || 0,
        agentid: fbSelectedAgent.AgentID,
        date_added: calgaryDateString(),
      });
      setNewFbDescription("");
      setNewFbAmount("");
      setNewFbTaxPercent("0");
      setFbSelectedAgent(null);
      setFbAgentSearch("");
      setShowCreateFb(false);
      refetchFb();
      toastService.success("Fixed bill created successfully.");
    } catch (err: any) {
      console.error("Error creating fixed bill:", err);
      const msg = err.message || "Failed to create fixed bill.";
      setError(msg);
      toastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFixedBill = async (item: any) => {
    if (!selectedMatter) {
      setError("Please select a matter first");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FixBillService.updateFixBillExpense(String(item.fbill_id), {
        description: item.description,
        amount: item.amount,
        tax_percent: item.tax_percent || 0,
        agentid: item.agentid,
        matterid: selectedMatter.matterid,
        date_added: item.date_added || calgaryDateString(),
      });
      toastService.success("Fixed bill assigned successfully.");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error assigning fixed bill:", err);
      const msg = err.message || "Failed to assign fixed bill.";
      setError(msg);
      toastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!selectedMatter) {
        setError("Please select a matter");
        setLoading(false);
        return;
      }

      if (!selectedAgent) {
        setError("Please select an agent");
        setLoading(false);
        return;
      }

      if (!description || !amount) {
        setError("Please fill in all required fields");
        setLoading(false);
        return;
      }

      const formData: any = {
        description,
        amount: parseFloat(amount),
        tax_percent: parseFloat(taxPercent) || 0,
        date_added: date,
        agentid: selectedAgent.AgentID,
        matterid: selectedMatter.matterid,
      };

      // Add invoiceid for General expenses only
      if (expenseType === "general") {
        formData.invoiceid = null;
      }

      // Call appropriate service based on expense type
      if (expenseType === "general") {
        await GenExpensesService.addGenExpense(formData);
      } else if (expenseType === "operating") {
        await OppExpService.addOppExpense(formData);
      } else if (expenseType === "vendorServices") {
        await VenExpService.addVenExpense(formData);
      }

      toastService.success("Expense entry added successfully.");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error adding expense entry:", error);
      const errorMessage =
        error.message || "Failed to add expense entry. Please try again.";
      setError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Expense Entry"
      size="medium"
    >
      {/* Expense Type Selection */}
      <div className="log-expense-entry-type-tabs">
        <button
          className={`log-expense-entry-tab ${
            expenseType === "general" ? "active" : ""
          }`}
          onClick={() => setExpenseType("general")}
        >
          General
        </button>
        <button
          className={`log-expense-entry-tab ${
            expenseType === "operating" ? "active" : ""
          }`}
          onClick={() => setExpenseType("operating")}
        >
          Operating
        </button>
        <button
          className={`log-expense-entry-tab ${
            expenseType === "vendorServices" ? "active" : ""
          }`}
          onClick={() => setExpenseType("vendorServices")}
        >
          Vendor Services
        </button>
        <button
          className={`log-expense-entry-tab ${
            expenseType === "fixedBills" ? "active" : ""
          }`}
          onClick={() => setExpenseType("fixedBills")}
        >
          Fixed Bills
        </button>
      </div>

      {error && <div className="log-expense-entry-error">{error}</div>}

      {/* Fixed Bills tab: catalog-based UI */}
      {expenseType === "fixedBills" && (
        <div className="log-expense-fb-section">
          <div className="log-expense-entry-field">
            <label>Matter</label>
            <div className="log-expense-entry-search-container">
              <input
                ref={matterInputRef}
                type="text"
                placeholder="Search matters..."
                value={matterSearch}
                onChange={(e) => {
                  setMatterSearch(e.target.value);
                  setShowMatterDropdown(true);
                }}
                onFocus={() => setShowMatterDropdown(true)}
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              {showMatterDropdown && filteredMatters.length > 0 && (
                <div ref={matterDropdownRef} className="log-expense-entry-dropdown">
                  {filteredMatters.slice(0, 10).map((matter) => (
                    <div
                      key={matter.matterid}
                      className="log-expense-entry-dropdown-item"
                      onClick={() => handleMatterSelect(matter)}
                    >
                      {matter.matterid}: {matter.mattername || ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="log-expense-entry-field">
            <label>Search Fixed Bills</label>
            <input
              type="text"
              value={fbSearch}
              onChange={(e) => setFbSearch(e.target.value)}
              placeholder="Filter by description..."
            />
          </div>

          <div className="log-expense-fb-create-toggle">
            <button
              type="button"
              className="log-expense-fb-create-btn"
              onClick={() => setShowCreateFb(!showCreateFb)}
            >
              {showCreateFb ? "Cancel" : "Create New Fixed Bill"}
            </button>
          </div>

          {showCreateFb && (
            <form
              className="log-expense-fb-create-form"
              onSubmit={handleCreateFixedBill}
            >
              <div className="log-expense-entry-field">
                <label>Agent</label>
                <div className="log-expense-entry-search-container">
                  <input
                    ref={fbAgentInputRef}
                    type="text"
                    placeholder="Search agents..."
                    value={fbAgentSearch}
                    onChange={(e) => {
                      setFbAgentSearch(e.target.value);
                      setShowFbAgentDropdown(true);
                    }}
                    onFocus={() => setShowFbAgentDropdown(true)}
                    required
                  />
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  {showFbAgentDropdown && filteredFbAgents.length > 0 && (
                    <div ref={fbAgentDropdownRef} className="log-expense-entry-dropdown">
                      {filteredFbAgents.slice(0, 10).map((agent) => (
                        <div
                          key={agent.AgentID}
                          className="log-expense-entry-dropdown-item"
                          onClick={() => handleFbAgentSelect(agent)}
                        >
                          {getAgentDisplayName(agent)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="log-expense-entry-field">
                <label>Description</label>
                <input
                  type="text"
                  value={newFbDescription}
                  onChange={(e) => setNewFbDescription(e.target.value)}
                  placeholder="e.g. Monthly Office Rent"
                  required
                />
              </div>
              <div className="log-expense-entry-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newFbAmount}
                  onChange={(e) => setNewFbAmount(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="log-expense-entry-field">
                <label>Tax Percent (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newFbTaxPercent}
                  onChange={(e) => setNewFbTaxPercent(e.target.value)}
                  placeholder="0"
                />
              </div>
              <button
                type="submit"
                className="log-expense-entry-save"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faCheck} />
                Save Fixed Bill
              </button>
            </form>
          )}

          {fbLoading && (
            <p className="log-expense-entry-hint">Loading fixed bills...</p>
          )}
          {fbError && (
            <div className="log-expense-entry-error">Error loading fixed bills.</div>
          )}

          <div className="log-expense-fb-list">
            {filteredFb.length === 0 && !fbLoading && (
              <p className="log-expense-fb-empty">No fixed bills found. Create one above to get started.</p>
            )}
            {filteredFb.map((item: any) => (
              <div
                key={item.fbill_id}
                className="log-expense-fb-item"
              >
                <div className="log-expense-fb-item-info">
                  <span className="log-expense-fb-description">{item.description}</span>
                  {item.matter_name && (
                    <span className="log-expense-fb-matter">{item.matter_name}</span>
                  )}
                </div>
                <div className="log-expense-fb-item-right">
                  <span className="log-expense-fb-amount">${item.amount}</span>
                  <button
                    type="button"
                    className="log-expense-fb-assign-btn"
                    disabled={loading}
                    onClick={() => handleAssignFixedBill(item)}
                    title="Assign to this matter"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form for General, Operating, and Vendor Services tabs */}
      {expenseType !== "fixedBills" && (
        <form onSubmit={handleSubmit} className="log-expense-entry-form">
          {/* Common Fields */}
          <div className="log-expense-entry-field">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="log-expense-entry-field">
            <label>Matter</label>
            <div className="log-expense-entry-search-container">
              <input
                ref={matterInputRef}
                type="text"
                placeholder="Search matters..."
                value={matterSearch}
                onChange={(e) => {
                  setMatterSearch(e.target.value);
                  setShowMatterDropdown(true);
                }}
                onFocus={() => setShowMatterDropdown(true)}
                required
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              {showMatterDropdown && filteredMatters.length > 0 && (
                <div ref={matterDropdownRef} className="log-expense-entry-dropdown">
                  {filteredMatters.slice(0, 10).map((matter) => (
                    <div
                      key={matter.matterid}
                      className="log-expense-entry-dropdown-item"
                      onClick={() => handleMatterSelect(matter)}
                    >
                      {matter.matterid}: {matter.mattername || ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="log-expense-entry-field">
            <label>Agent</label>
            <div className="log-expense-entry-search-container">
              <input
                ref={agentInputRef}
                type="text"
                placeholder="Search agents..."
                value={agentSearch}
                onChange={(e) => {
                  setAgentSearch(e.target.value);
                  setShowAgentDropdown(true);
                }}
                onFocus={() => setShowAgentDropdown(true)}
                required
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              {showAgentDropdown && filteredAgents.length > 0 && (
                <div ref={agentDropdownRef} className="log-expense-entry-dropdown">
                  {filteredAgents.slice(0, 10).map((agent) => (
                    <div
                      key={agent.AgentID}
                      className="log-expense-entry-dropdown-item"
                      onClick={() => handleAgentSelect(agent)}
                    >
                      {getAgentDisplayName(agent)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="log-expense-entry-field">
            <label>Description</label>
            <FormEntryMultilineTextbox
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter expense description..."
              rows={4}
            />
          </div>

          <div className="log-expense-entry-field">
            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="log-expense-entry-field">
            <label>Tax Percent (Optional)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* Action Buttons */}
          <div className="log-expense-entry-actions">
            <button
              type="button"
              className="log-expense-entry-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="log-expense-entry-save"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faCheck} />
              Save to Queue
            </button>
          </div>
        </form>
      )}
    </FormModal>
  );
};

export default LogExpenseEntryModal;
